import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectCreditCardsFullPaginatedData,
  selectFilteredCreditCardOptions,
  selectFilteredRecentUserEmailOptions,
  selectFilteredUserEmailOptions,
  selectUsersFullPaginatedData,
} from 'src/app/RequestBudget/redux/requestBudget.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { RequestBudgetToastMessage } from 'src/app/Toast/Toast'
import BudgetPickContent from 'src/components/BudgetPickContent/BudgetPickContent'
import BudgetRecurringContent from 'src/components/BudgetRecurringContent/BudgetRecurringContent'
import BudgetStandardContent from 'src/components/BudgetStandardContent/BudgetStandardContent'
import { CreditCardDropdownInfiniteScrollProps } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useFlyoutDispatch } from 'src/hooks'
import { CreditCardStatus, CreditCardType } from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import { closeModal } from 'src/navigation/Lnk'
import {
  budgetRequestsMutations,
  budgetRequestsQueryKeys,
} from 'src/queries/budgetRequests/budgetRequests.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import {
  getCreditCard,
  getCreditCards,
} from 'src/redux/actions/data/creditCards.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'
import { parseBackendErrors } from 'src/utils/error.utils'

export enum BudgetScreen {
  Pick,
  Standard,
  Recurring,
}

const RequestBudget: React.FC = () => {
  const queryParams = useSelector(selectQueryParams)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredUserEmailOptions = useSelector(selectFilteredUserEmailOptions)
  const filteredRecentUserEmailOptions = useSelector(
    selectFilteredRecentUserEmailOptions,
  )
  const creditCardsFullPaginatedData = useSelector(
    selectCreditCardsFullPaginatedData,
  )
  const filteredCreditCardOptions = useSelector(selectFilteredCreditCardOptions)
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount,
    ] as const,
  })
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount =
    statisticsResults[
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0
  const appName = useSelector(selectAppName)

  const dispatch = useFlyoutDispatch()
  const queryClient = useQueryClient()
  const postBudgetRequest = useMutation({
    ...budgetRequestsMutations.post(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: budgetRequestsQueryKeys.all(),
      })
      const {
        approver: { firstName, lastName },
        displayName,
      } = data.budgetRequest
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <RequestBudgetToastMessage
              budgetName={displayName}
              user={`${firstName} ${lastName}`}
            />
          ),
        }),
      )
      dispatch(closeModal())
    },
  })
  const postingCreditCard = postBudgetRequest.isPending
  const errors = postBudgetRequest.error
    ? parseBackendErrors(postBudgetRequest.error)
    : undefined
  const topLevelError = errors?.topLevelError
  const errorDetailMap = errors?.errorDetailMap

  const dispatchPostCreditCardAndOpenToastAndCloseFlyout = (
    body: Partial<PostCreditCardRequest>,
  ): void => {
    postBudgetRequest.mutate({ body })
  }
  const [screen, setScreen] = useState(BudgetScreen.Pick)

  useEffect((): void => {
    if (typeof queryParams['creditCardId'] === 'string') {
      dispatch(getCreditCard(queryParams['creditCardId']))
    }
  }, [queryParams['creditCardId']])

  const initialFlatFields = {
    // TODO: refactor all uses of queryParams to handle arrays and normalize to string[]
    creditCardId:
      typeof queryParams.creditCardId === 'string'
        ? queryParams.creditCardId
        : undefined,
  }

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers(
        {
          q: filterInput,
          count: 10,
          page: page,
        },
        true,
      ),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  const fetchCreditCards = (filterInput?: string, page?: number): void => {
    dispatch(
      getCreditCards({
        search: filterInput,
        count: 10,
        page: page,
        status: CreditCardStatus.Active,
        sortField: 'type',
        sortDirection: 'ASC',
        type: CreditCardType.Source,
        withPermission: CreditCardPermissions.IssueDelegateCreditCards,
        budgetsEnabled: true,
      }),
    )
  }
  const creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps = {
    fetch: fetchCreditCards,
    ...creditCardsFullPaginatedData,
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {!statisticsResults[
        StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
      ].isSuccess ? (
        <LoadingBackgroundPage />
      ) : (
        <>
          <Helmet>
            <title>{`Request a Budget | ${appName}`}</title>
            <meta
              name="description"
              content={`Request a Budget page of ${appName}`}
            />
          </Helmet>
          {screen === BudgetScreen.Pick && (
            <BudgetPickContent setScreen={setScreen} requesting />
          )}
          {screen === BudgetScreen.Standard && (
            <BudgetStandardContent
              postCreditCardOrBudgetRequest={
                dispatchPostCreditCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              postingCreditCard={postingCreditCard}
              initialFlatFields={initialFlatFields}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              rcnOptions={filteredCreditCardOptions}
              creditCardInfiniteScrollProps={creditCardInfiniteScrollProps}
              isFormDisabled={
                creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
              }
              requesting
            />
          )}
          {screen === BudgetScreen.Recurring && (
            <BudgetRecurringContent
              postCreditCard={dispatchPostCreditCardAndOpenToastAndCloseFlyout}
              setScreen={setScreen}
              postingCreditCard={postingCreditCard}
              initialFlatFields={initialFlatFields}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              rcnOptions={filteredCreditCardOptions}
              creditCardInfiniteScrollProps={creditCardInfiniteScrollProps}
              isFormDisabled={
                creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
              }
              requesting
            />
          )}
        </>
      )}
    </RightFlyout>
  )
}

export default RequestBudget
